import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { CONTACT, NAV_LINKS, scrollToId } from "../../lib/site";
import { Reveal } from "../../lib/animations";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in all fields.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Thank you — Cibele will be in touch soon.");
      setForm({ name: "", email: "", message: "" });
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <footer
      id="contact"
      data-testid="footer-section"
      className="grain relative overflow-hidden bg-espresso px-6 pt-28 pb-12 md:px-12 md:pt-40"
    >
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_20%_0%,rgba(87,15,55,0.6),transparent_55%)]" />

      <div className="relative mx-auto max-w-[1600px]">
        <Reveal>
          <p className="font-sans text-[0.7rem] uppercase tracking-[0.4em] text-peach/70">
            Get in touch
          </p>
          <h2 className="mt-4 font-serif text-6xl font-light leading-[0.9] tracking-tight text-champagne md:text-[12vw]">
            Let&apos;s <span className="italic text-goldlight">talk</span>.
          </h2>
        </Reveal>

        <div className="mt-16 grid grid-cols-1 gap-16 lg:grid-cols-12">
          {/* Contact info */}
          <div className="lg:col-span-5">
            <Reveal>
              <div className="space-y-8">
                <div>
                  <p className="font-sans text-[0.65rem] uppercase tracking-[0.3em] text-peach/60">
                    Email
                  </p>
                  <a
                    href={`mailto:${CONTACT.email}`}
                    data-testid="footer-email"
                    className="font-serif text-2xl text-champagne transition-colors duration-300 hover:text-goldlight md:text-3xl"
                  >
                    {CONTACT.email}
                  </a>
                </div>
                <div>
                  <p className="font-sans text-[0.65rem] uppercase tracking-[0.3em] text-peach/60">
                    Phone · WhatsApp
                  </p>
                  <a
                    href={CONTACT.whatsappHref}
                    target="_blank"
                    rel="noreferrer"
                    data-testid="footer-whatsapp"
                    className="font-serif text-2xl text-champagne transition-colors duration-300 hover:text-goldlight md:text-3xl"
                  >
                    {CONTACT.whatsapp}
                  </a>
                </div>
                <div>
                  <p className="font-sans text-[0.65rem] uppercase tracking-[0.3em] text-peach/60">
                    Based in
                  </p>
                  <p className="font-serif text-xl text-champagne/90">{CONTACT.city}</p>
                </div>
              </div>
            </Reveal>
          </div>

          {/* Form */}
          <div className="lg:col-span-7">
            <Reveal delay={0.1}>
              <form onSubmit={submit} data-testid="contact-form" className="space-y-6">
                <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                  <input
                    data-testid="contact-name"
                    type="text"
                    placeholder="Your name"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="w-full border-b border-champagne/25 bg-transparent py-4 font-sans text-champagne placeholder:text-champagne/40 outline-none transition-colors duration-300 focus:border-goldlight"
                  />
                  <input
                    data-testid="contact-email"
                    type="email"
                    placeholder="Your email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    className="w-full border-b border-champagne/25 bg-transparent py-4 font-sans text-champagne placeholder:text-champagne/40 outline-none transition-colors duration-300 focus:border-goldlight"
                  />
                </div>
                <textarea
                  data-testid="contact-message"
                  rows={4}
                  placeholder="Tell us about your event or enquiry"
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  className="w-full resize-none border-b border-champagne/25 bg-transparent py-4 font-sans text-champagne placeholder:text-champagne/40 outline-none transition-colors duration-300 focus:border-goldlight"
                />
                <button
                  data-testid="contact-submit"
                  type="submit"
                  disabled={loading}
                  className="group inline-flex items-center gap-3 rounded-full bg-terracotta px-10 py-4 font-sans text-xs uppercase tracking-[0.24em] text-espresso transition-colors duration-300 hover:bg-goldlight disabled:opacity-60"
                >
                  {loading ? "Sending..." : "Send message"}
                  <span className="inline-block transition-transform duration-300 group-hover:translate-x-1">→</span>
                </button>
              </form>
            </Reveal>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="mt-28 flex flex-col items-center justify-between gap-6 border-t border-champagne/15 pt-8 md:flex-row">
          <img src="/logo-gold.png" alt="Cibele" className="h-12 w-auto opacity-95" />
          <div className="flex flex-wrap items-center justify-center gap-6">
            {NAV_LINKS.map((l) => (
              <a
                key={l.href}
                href={l.href}
                data-testid={`footer-link-${l.label.toLowerCase()}`}
                onClick={(e) => {
                  e.preventDefault();
                  scrollToId(l.href);
                }}
                className="font-sans text-[0.65rem] uppercase tracking-[0.25em] text-champagne/60 transition-colors duration-300 hover:text-goldlight"
              >
                {l.label}
              </a>
            ))}
          </div>
          <p className="font-sans text-[0.6rem] uppercase tracking-[0.25em] text-champagne/40">
            © {new Date().getFullYear()} Cibele Belly Dance
          </p>
        </div>
      </div>
    </footer>
  );
};
